/* Figures: a range bar, a pager, a line chart and a table.
 *
 * The chart tells its series apart by weight and a labelled end rather than by
 * colour, which is the same reason the desks only use colour for four meanings.
 * A reader who cannot separate two hues can still separate two lines.
 *
 * Nothing here computes anything a server should have computed. The chart is
 * handed its numbers and draws them; the pager is handed a total and reports
 * it. Both desks still have to decide cursor or offset before Pager is wired to
 * a real read — the handoff says so under "Still open" and it is still open.
 */

import { useEffect } from "react";
import type { ReactNode } from "react";

import { Info, Seg } from "./kit.js";

/** How much of the run is being looked at. */
export type Range = "The whole run" | "Today" | "Last 7 days" | "Custom";

const RANGES: readonly Range[] = ["The whole run", "Today", "Last 7 days", "Custom"];

/**
 * The range every figure on a page is read over.
 *
 * The note under it is not decoration. Nothing on a dashboard updates on its
 * own, and a page of numbers that looks live and is not is worse than one that
 * says so.
 */
export function RangeBar({
  value,
  onPick,
  from,
  to,
  setFrom,
  setTo,
  note = "Every figure below is read at the moment this page loads, over the range above. Nothing on this page is live.",
}: {
  value: Range;
  onPick: (range: Range) => void;
  from: string;
  to: string;
  setFrom: (date: string) => void;
  setTo: (date: string) => void;
  note?: string;
}): JSX.Element {
  return (
    <div className="panel panel-stack">
      <div className="filters">
        <div className="field">
          <span className="label">Range</span>
          {/* The kit's Seg, which is what the prototype's Seg2 resolves to
              (desk-kit.jsx): its own width, and a select on a narrow frame. */}
          <Seg label="Range" value={value} options={RANGES} onPick={onPick} />
        </div>
        {value === "Custom" ? (
          <>
            <label className="field">
              <span className="label">From</span>
              <input
                className="input"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </label>
            <label className="field">
              <span className="label">To</span>
              <input
                className="input"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </label>
          </>
        ) : null}
      </div>
      <p className="help help-tight">{note}</p>
    </div>
  );
}

/** The rows of `rows` that belong on `page`, for a list drawn under a Pager. */
// Clamped here as well as in the Pager: the Pager pulls `page` back after a
// render, so a list that shrinks would otherwise slice past its end once.
export function pageOf<T>(rows: readonly T[], page: number, per: number): T[] {
  const at = Math.min(page, Math.max(1, Math.ceil(rows.length / per)));
  return rows.slice((at - 1) * per, at * per);
}

/**
 * Which slice of a long list is on screen, and how to reach the others. Every
 * list in the admin ends on this one footer.
 *
 * `note` is what the page would otherwise say in a paragraph under the list —
 * a cap, a rule, why a button is missing. It sits behind the (i) next to the
 * count: it is asked for, not read past on every visit.
 *
 * TWO LAYOUTS, CHOSEN BY THE PAGER'S OWN WIDTH (furniture.css, "the pager").
 * `.pager` is the container and `.pg-in` the grid inside it, because a
 * container cannot be laid out by its own size. The count's last word and
 * its (i) are one unbreakable piece (`.pg-u`), so a count that has to wrap on
 * a small phone never leaves the (i) alone on a line.
 */
export function Pager({
  page,
  per,
  setPer,
  setPage,
  total,
  unit,
  note,
}: {
  page: number;
  per: number;
  setPer: (per: number) => void;
  setPage: (page: number) => void;
  total: number;
  unit: string;
  note?: ReactNode;
}): JSX.Element {
  const pages = Math.max(1, Math.ceil(total / per));
  // A list that shrinks under the reader (a filter, an approval, a refetch)
  // would otherwise leave them on a page past the end, looking at nothing.
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages, setPage]);
  const at = Math.min(page, pages);
  const first = total === 0 ? 0 : (at - 1) * per + 1;
  const last = Math.min(total, at * per);
  return (
    <div className="pager">
      <div className="pg-in">
        <label className="pg-per">
          <span>Per page</span>
          <select
            className="input"
            value={per}
            onChange={(event) => {
              setPer(Number(event.target.value));
              setPage(1);
            }}
          >
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <div className="pg-n">
          Showing {first}–{last} of {total}{" "}
          <span className="pg-u">
            {unit}
            {note !== undefined && note !== null && note !== false ? <Info label={`About these ${unit}`}>{note}</Info> : null}
          </span>
        </div>
        <div className="pg-b">
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            disabled={at === 1}
            onClick={() => setPage(at - 1)}
          >
            Back
          </button>
          <span className="pg-at">
            {at} of {pages}
          </span>
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            disabled={at >= pages}
            onClick={() => setPage(at + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

/** One line on a chart: a name for the legend and a value per label on the x axis. */
export interface Series {
  name: string;
  values: readonly number[];
}

/** One SVG, no library, no colour. */
export function LineChart({
  title,
  aside,
  x,
  series,
  unit,
  label,
}: {
  title: string;
  aside?: string;
  x: readonly string[];
  series: readonly Series[];
  unit?: string;
  /** What a screen reader hears for the drawing, when the title is not enough. */
  label?: string;
}): JSX.Element {
  const width = 660;
  const height = 240;
  const left = 46;
  const right = 26;
  const top = 18;
  const bottom = 34;

  // The tallest value on any series, floored at 1 so an all-zero chart still
  // has an axis rather than dividing by nothing.
  const peak = Math.max(1, ...series.flatMap((s) => [...s.values]));
  const step = (width - left - right) / Math.max(1, x.length - 1);
  const px = (i: number): number => left + i * step;
  const py = (value: number): number => top + (1 - value / peak) * (height - top - bottom);

  return (
    <div className="chart">
      <div className="chart-h">
        <p className="chart-t">{title}</p>
        {aside !== undefined ? <p className="chart-a">{aside}</p> : null}
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={label ?? title}
        preserveAspectRatio="xMidYMid meet"
      >
        {[0, 0.5, 1].map((tick) => (
          <g key={tick}>
            <line
              x1={left}
              x2={width - right}
              y1={py(peak * tick)}
              y2={py(peak * tick)}
              className="ch-grid"
            />
            <text x={left - 10} y={py(peak * tick) + 4} className="ch-ax" textAnchor="end">
              {Math.round(peak * tick).toLocaleString()}
            </text>
          </g>
        ))}
        {/* Keyed by position as well as label: two weeks of different years
            share a label like "w3", and a repeated key made React drop one
            (Bugbot, #124). */}
        {x.map((label, i) => (
          <text key={`${i}-${label}`} x={px(i)} y={height - 10} className="ch-ax" textAnchor="middle">
            {label}
          </text>
        ))}
        {series.map((s, index) => (
          <g key={s.name} className={`ch-s ch-s${index}`}>
            <polyline
              points={s.values.map((value, i) => `${px(i)},${py(value)}`).join(" ")}
              className="ch-line"
            />
            {s.values.map((value, i) => (
              <circle key={i} cx={px(i)} cy={py(value)} r="2.6" className="ch-dot" />
            ))}
          </g>
        ))}
      </svg>
      <div className="ch-legend">
        {series.map((s, index) => (
          <span key={s.name} className={`ch-key ch-s${index}`}>
            <svg viewBox="0 0 26 8" aria-hidden="true">
              <line x1="1" y1="4" x2="25" y2="4" className="ch-line" />
            </svg>
            {s.name}
          </span>
        ))}
      </div>
      {unit !== undefined ? <p className="help help-unit">{unit}</p> : null}
    </div>
  );
}

/**
 * A table of figures.
 *
 * Every cell carries its column name in `data-label`, which is what lets the
 * whole table become stacked blocks under 760px instead of scrolling sideways.
 * The first column is the row's subject and stays left; the rest are numbers
 * and align right.
 */
export function DataTable({
  cols,
  rows,
  caption,
}: {
  cols: readonly string[];
  rows: ReadonlyArray<readonly ReactNode[]>;
  caption?: string;
}): JSX.Element {
  return (
    <div className="tablewrap">
      <div className="tablescroll">
        <table>
          {caption !== undefined ? <caption>{caption}</caption> : null}
          <thead>
            <tr>
              {cols.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j} data-label={cols[j]} className={j === 0 ? undefined : "num"}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
